import { Input, KeyCode } from "../../lib/Input";
import MonoBehavior from "../../lib/MonoBehavior";
import { Time } from "../../lib/Time";
import Vector2 from "../../math/Vector2";
import { clamp01 } from "../../util";
import Color from "../../util/Color";
import { Random } from "../../util/Random";
import Explosion from "../Explosion";
import SPAbility from "../SPAbility";
import SPBogey from "../SPBogey";
import SPGameManager from "../SPGameManager";
import SPPlayer from "../SPPlayer";

enum State {
    Moving,
    Active
}

export class GravitonPulseBehavior extends MonoBehavior {
    targetPosition: Vector2 = Vector2.zero;
    size = 256;
    lifetime = 5;
    state: State = State.Moving;
    velocity: Vector2 = Vector2.zero;
    dots: Vector2[] = [];

    Die() {
        this.engine.NewGameObjectWithScript(Explosion, {
            position: this.transform.position,
            initialValues: {
                size: this.size/2,
                w: this.size,
                color: new Color(80,255,80)
            }
        });

        for (const bogey of this.engine.GetGameObjectsBehaviors<SPBogey>(SPBogey).filter(
            v => Vector2.distance(v.transform.position, this.transform.position) <= this.size/2 + v.size
        )) {
            bogey.Damage(4);
            const vec = bogey.transform.position.sub(this.transform.position).normalized;
            bogey.velocity = bogey.velocity.add(vec.scale(300));
        }

        this.engine.RemoveGameObject(this.gameObject);
    }

    MovingUpdate() {
        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));

        if (Vector2.distance(this.transform.position, this.targetPosition) <= 8) {
            this.engine.NewGameObjectWithScript(Explosion, {
                position: this.transform.position,
                initialValues: {
                    size: this.size/2,
                    w: this.size,
                    color: new Color(80,255,80)
                }
            });

            for (const bogey of this.engine.GetGameObjectsBehaviors<SPBogey>(SPBogey).filter(
                v => Vector2.distance(v.transform.position, this.transform.position) <= this.size/2 + v.size
            )) {
                bogey.Damage(4);
            }

            this.state = State.Active;
        }
    }

    ActiveUpdate() {
        this.lifetime -= Time.deltaTime;

        if (this.lifetime <= 0) {
            this.Die();
            return;
        }

        if (this.dots.length < 200) {
            const rt = Random.float(0, 2 * Math.PI);
            const vec = Vector2.fromRadians(rt).scale(Random.int(16, this.size));
            this.dots.push(this.transform.position.add(vec));
        }

        for (let i = this.dots.length - 1; i >= 0; i--) {
            const dot = this.dots[i];
            const travelVec = this.transform.position.sub(dot);
            this.dots[i] = dot.add(travelVec.scale(Time.deltaTime));

            if (Vector2.distance(this.dots[i], this.transform.position) <= 4) {
                this.dots.splice(i, 1);
            }
        }
    }

    Update(): void {
        if (!SPGameManager.Singleton.isInGame) return;

        switch (this.state) {
            case State.Moving:
                this.MovingUpdate();
                break;
            case State.Active:
                this.ActiveUpdate();
                break;
        
            default:
                break;
        }
    }

    Draw(): void {
        if (this.state === State.Active) {
            this.ctx.strokeStyle = '#55ff55';
            this.ctx.lineWidth = 1;
            this.engine.StrokeCircle(this.transform.position, this.size);

            this.engine.StrokeCircle(this.transform.position, this.size * (this.lifetime/5));

            this.ctx.fillStyle = '#ffffff55';
            for (const dot of this.dots) {
                this.engine.FillCircle(dot, 1);
            }
        } else {
            this.ctx.fillStyle = 'white';
            this.engine.FillCircle(this.transform.position, 2);
        }
    }

}

export default class GravitonPulse extends SPAbility {
    size = 256;

    constructor(player: SPPlayer) {
        super(player, {
            cooldown: 35,
            duration: 5,
            icon: 4,
            trigger: KeyCode.Digit1,
            triggerType: Input.GetKeyUp
        });
    }

    onUse(): void {
        this.player.engine.NewGameObjectWithScript(GravitonPulseBehavior, {
            position: this.player.transform.position,
            initialValues: {
                targetPosition: this.player.engine.mouseWorldPosition,
                size: this.size,
                lifetime: this.duration,
                velocity: this.player.engine.mouseWorldPosition.sub(this.player.transform.position).normalized.scale(300)
            }
        });
    }

    onKeydownDraw(): void {
        const ctx = this.player.ctx;
        const engine = this.player.engine;
        ctx.fillStyle = '#55ff5520';
        engine.FillCircle(engine.mouseWorldPosition, this.size);
        engine.FillCircle(engine.mouseWorldPosition, 128);
    }
}